import { isSupportTicketListItem } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import type { ActionDefinition } from "@redotech/redo-web/actions/action-definition";
import type { ActionId } from "@redotech/redo-web/actions/action-ids";
import {
  isSupportTicketActionId,
  supportTicketActionIds,
  type SupportTicketActionId,
} from "@redotech/redo-web/actions/support-ticket-actions/action-ids";
import { supportTicketActionDefinitions } from "@redotech/redo-web/actions/support-ticket-actions/definitions";

// Helper functions for visibility checks - accept unknown for type safety
const isNotDeleted = (context: unknown) =>
  isSupportTicketListItem(context) && context.status !== "DELETED";

const isNotDeletedOrArchived = (context: unknown) =>
  isSupportTicketListItem(context) &&
  context.status !== "DELETED" &&
  context.status !== "ARCHIVED";

/**
 * Full action registry with domain-specific isEnabled/isVisible logic.
 * Merges static definitions from redo-web with context-dependent checks.
 * Uses unknown context type for compatibility with global action scope provider.
 */
export const actionRegistry: Record<
  SupportTicketActionId,
  ActionDefinition<unknown, SupportTicketActionId>
> = {
  // Table navigation - no visibility/enabled checks needed
  [supportTicketActionIds.navigateUp]: {
    ...supportTicketActionDefinitions.navigateUp,
  },
  [supportTicketActionIds.navigateDown]: {
    ...supportTicketActionDefinitions.navigateDown,
  },
  [supportTicketActionIds.openSupportTicket]: {
    ...supportTicketActionDefinitions.openSupportTicket,
  },
  [supportTicketActionIds.openActionMenu]: {
    ...supportTicketActionDefinitions.openActionMenu,
  },

  // Direct support ticket actions with context-specific checks
  [supportTicketActionIds.close]: {
    ...supportTicketActionDefinitions.close,
    isEnabled: (context) =>
      isSupportTicketListItem(context) && context.status !== "CLOSED",
    isVisible: isNotDeletedOrArchived,
  },
  [supportTicketActionIds.reopen]: {
    ...supportTicketActionDefinitions.reopen,
    isEnabled: (context) =>
      isSupportTicketListItem(context) &&
      (context.status === "CLOSED" || context.status === "SNOOZED"),
    isVisible: isNotDeletedOrArchived,
  },
  [supportTicketActionIds.markInProgress]: {
    ...supportTicketActionDefinitions.markInProgress,
    isEnabled: (context) =>
      isSupportTicketListItem(context) && context.status !== "IN_PROGRESS",
    isVisible: isNotDeletedOrArchived,
  },
  [supportTicketActionIds.snooze]: {
    ...supportTicketActionDefinitions.snooze,
    isEnabled: (context) =>
      isSupportTicketListItem(context) &&
      context.status !== "SNOOZED" &&
      context.status !== "CLOSED",
    isVisible: isNotDeletedOrArchived,
  },
  [supportTicketActionIds.archive]: {
    ...supportTicketActionDefinitions.archive,
    isEnabled: (context) =>
      isSupportTicketListItem(context) && context.status !== "ARCHIVED",
    isVisible: isNotDeleted,
  },
  [supportTicketActionIds.delete]: {
    ...supportTicketActionDefinitions.delete,
    isEnabled: (context) =>
      isSupportTicketListItem(context) && context.status !== "DELETED",
  },
  [supportTicketActionIds.markRead]: {
    ...supportTicketActionDefinitions.markRead,
    isEnabled: (context) =>
      isSupportTicketListItem(context) && context.readAt === null,
  },
  [supportTicketActionIds.markUnread]: {
    ...supportTicketActionDefinitions.markUnread,
    isEnabled: (context) =>
      isSupportTicketListItem(context) && context.readAt !== null,
  },
  [supportTicketActionIds.markSpam]: {
    ...supportTicketActionDefinitions.markSpam,
    isVisible: isNotDeletedOrArchived,
  },
  [supportTicketActionIds.unmarkSpam]: {
    ...supportTicketActionDefinitions.unmarkSpam,
    isVisible: isNotDeletedOrArchived,
  },

  // Submenu trigger actions
  [supportTicketActionIds.setPriority]: {
    ...supportTicketActionDefinitions.setPriority,
    isVisible: isNotDeletedOrArchived,
  },
  [supportTicketActionIds.assign]: {
    ...supportTicketActionDefinitions.assign,
    isVisible: isNotDeletedOrArchived,
  },
  [supportTicketActionIds.assignToMe]: {
    ...supportTicketActionDefinitions.assignToMe,
    isVisible: isNotDeletedOrArchived,
  },
  [supportTicketActionIds.addTag]: {
    ...supportTicketActionDefinitions.addTag,
    isVisible: isNotDeleted,
  },

  // Priority options
  [supportTicketActionIds.priorityUrgent]: {
    ...supportTicketActionDefinitions.priorityUrgent,
    isVisible: isNotDeletedOrArchived,
  },
  [supportTicketActionIds.priorityClear]: {
    ...supportTicketActionDefinitions.priorityClear,
    isVisible: isNotDeletedOrArchived,
  },

  // Snooze options
  [supportTicketActionIds.snooze1h]: {
    ...supportTicketActionDefinitions.snooze1h,
    isVisible: isNotDeletedOrArchived,
  },
  [supportTicketActionIds.snooze4h]: {
    ...supportTicketActionDefinitions.snooze4h,
    isVisible: isNotDeletedOrArchived,
  },
  [supportTicketActionIds.snooze24h]: {
    ...supportTicketActionDefinitions.snooze24h,
    isVisible: isNotDeletedOrArchived,
  },
  [supportTicketActionIds.snoozeCustom]: {
    ...supportTicketActionDefinitions.snoozeCustom,
    isVisible: isNotDeletedOrArchived,
  },
  [supportTicketActionIds.snoozeClear]: {
    ...supportTicketActionDefinitions.snoozeClear,
    isVisible: isNotDeletedOrArchived,
  },

  // Merge action
  [supportTicketActionIds.mergeTicket]: {
    ...supportTicketActionDefinitions.mergeTicket,
    isVisible: isNotDeletedOrArchived,
  },

  // Message input actions - context will be provided by message input component
  [supportTicketActionIds.sendMessage]: {
    ...supportTicketActionDefinitions.sendMessage,
  },
  [supportTicketActionIds.deleteDraft]: {
    ...supportTicketActionDefinitions.deleteDraft,
  },

  // Reply mode actions
  [supportTicketActionIds.reply]: { ...supportTicketActionDefinitions.reply },
  [supportTicketActionIds.replyAll]: {
    ...supportTicketActionDefinitions.replyAll,
  },
  [supportTicketActionIds.forward]: {
    ...supportTicketActionDefinitions.forward,
  },
  [supportTicketActionIds.internalNote]: {
    ...supportTicketActionDefinitions.internalNote,
  },

  // Meta comment actions (Facebook/Instagram)
  // Note: These will need proper context typing when integrated with message components
  [supportTicketActionIds.hideComment]: {
    ...supportTicketActionDefinitions.hideComment,
  },
  [supportTicketActionIds.unhideComment]: {
    ...supportTicketActionDefinitions.unhideComment,
  },
  [supportTicketActionIds.deleteComment]: {
    ...supportTicketActionDefinitions.deleteComment,
  },
  [supportTicketActionIds.likeComment]: {
    ...supportTicketActionDefinitions.likeComment,
  },
  [supportTicketActionIds.unlikeComment]: {
    ...supportTicketActionDefinitions.unlikeComment,
  },
  [supportTicketActionIds.updateComment]: {
    ...supportTicketActionDefinitions.updateComment,
  },
};

export function getAction(id: ActionId): ActionDefinition<unknown, ActionId> {
  if (!isSupportTicketActionId(id)) {
    throw new Error(`Unknown action: ${id}`);
  }
  return actionRegistry[id];
}
